/**
 * A paper you name, fetched into your own sources.
 *
 * Until now the corpus took two things: text somebody pasted, and a file
 * somebody dropped. A researcher holding a DOI therefore had to go and be the
 * fetcher — open the resolver, find the open-access copy, select the body, paste
 * it back — which is a chore this app can do, and three of the wired rows already
 * know how. This module is that errand and nothing more: one identifier in, one
 * corpus document out.
 *
 * IT ENTERS THROUGH THE CORPUS DOOR, which is the whole reason it is allowed to
 * exist. Nothing here mints a `WebSource`; the paper lands as a document through
 * `addDoc`, is retrieved by the same local BM25 search as everything else the user
 * added, and is cited as a `PassageCite`. So a fetched paper cannot contribute a
 * refuting domain at any multiplicity, and invariant 2 is untouched however many
 * papers agree with each other. The header of `core/sources.ts` is the argument;
 * this file is one more caller of it.
 *
 * AND IT IS STORED, which is the one way this path differs from the check-time
 * gather in `liveSources.ts`. That gather is automatic, so its records are
 * ephemeral and spend no corpus budget — a user must not pay a budget they did not
 * ask to spend. Here the user typed an identifier, so the document is theirs:
 * `addDoc`'s caps, its duplicate rule and its refusals all apply exactly as they
 * do to a paste, and a refusal is surfaced in its own words rather than swallowed.
 *
 * NOTHING HERE CALLS A MODEL and nothing here is priced. Five keyless GETs at
 * most for a DOI, one for an arXiv id, each through `getJson`/`getXml` in
 * `liveSources.ts` so the timeout, the composed abort signal and the four failure
 * codes exist once rather than once per caller. A failure is `{ ok: false, why }`
 * in `LIVE_FAILURE_NOTE`'s voice — the sentence names the service and never the
 * code, because a reader can act on "Crossref refused the request" and on
 * precisely nothing else.
 *
 * NEVER A PARTIAL DOCUMENT PRESENTED AS FULL TEXT. What arrived decides what the
 * document says it is: the paper's own body, an abstract, or the record alone.
 * `provenance` is the first line of the stored text and therefore the first thing
 * a source check reads, so it names each half and where it came from, and it says
 * out loud when a lookup did not answer. An abstract silently standing in for a
 * paper, or an unanswered retraction lookup reading as a clean bill, are the two
 * ways this could overstate what was read; both are written down instead.
 */

import { DOC_CHARS, addDoc, listDocs, loadDoc, type CorpusDoc } from '../core/corpus'
import { calendarDay } from '../core/dates'
import { SOURCES, liveOriginUrl, wellFormed, type SourceDef } from '../core/sources'
import type { DocNotice } from '../core/types'
import {
  LIVE_FAILURE_NOTE,
  CUT_MARK,
  ABSTRACT_MARK,
  FLAGGED_MARK,
  RECONSTRUCTED_MARK,
  RECORD_MARK,
  RETRACTED_MARK,
  bareDoi,
  fetchRetractions,
  fromInvertedIndex,
  getJson,
  getXml,
  qs,
  stripJats,
  stripJatsFullText,
  type LiveFailureCode,
  type Transport,
} from './liveSources'

/**
 * The registry rows this module may call, read off `SOURCES` rather than written
 * here — `indexPiles.ts`'s rule, for its reason: a row's `origin` is the ONE host
 * its requests may go to, the string `csp.test.ts` pairs with `wired`, and its
 * `name` is what every failure sentence is written from, so renaming a row renames
 * it everywhere at once. Two spellings of one service are two services to a reader.
 */
const ROW = {
  crossref: SOURCES.find((s) => s.id === 'crossref')!,
  openalex: SOURCES.find((s) => s.id === 'openalex')!,
  europepmc: SOURCES.find((s) => s.id === 'europepmc')!,
  arxiv: SOURCES.find((s) => s.id === 'arxiv')!,
}

/** Long enough for a cold public index, short enough that a press does not feel
 *  hung. The figure `liveSources.ts` gives one row, for its reason — and a paper
 *  costs at most three rounds of it, not four, because everything that depends
 *  only on the DOI is issued together. */
const PAPER_TIMEOUT_MS = 5000

/** `addDoc`'s own title clamp, mirrored so a disclosure mark is never what gets
 *  cut. `core/corpus.ts` slices a title to this before storing it; appending the
 *  marks AFTER clipping the paper's own title is `titleOf`'s rule in
 *  `liveSources.ts`, and for its reason — a retracted paper with a long title is
 *  two facts, and the title's tail is the one that may go. */
const DOC_TITLE_CHARS = 120

/**
 * The room the paper's own text gets, and why it is not simply `DOC_CHARS`.
 *
 * The stored document is `provenance + '\n\n' + text`, and `addDoc` refuses
 * anything over `DOC_CHARS` — so a full text cut to exactly that ceiling by
 * `stripJatsFullText` would arrive one line too long and be refused with an
 * arithmetic complaint about a document nobody assembled by hand. Reserving a
 * fixed headroom for the provenance line, rather than measuring it and cutting to
 * fit, keeps the two independent: measuring would make the cut depend on a
 * sentence that says whether the cut happened.
 */
export const PROVENANCE_CHARS = 400
/** The blank line between the two, which is what makes the provenance its own
 *  paragraph — and two characters of the document like any other, so it comes out
 *  of the room rather than out of the promise. */
const PARAGRAPH = '\n\n'
const PAPER_CHARS = DOC_CHARS - PROVENANCE_CHARS - PARAGRAPH.length

/** Authors printed before the list is called a crowd. A paper with two thousand
 *  authors is a real thing and its author list is not evidence about anything. */
const MAX_AUTHORS = 8

/* ── what a reference is ───────────────────────────────────────────────────── */

/** A reference this build knows how to fetch. Two kinds, because two services
 *  answer them and the answers differ in what they can carry. */
export type PaperRef = { kind: 'doi'; doi: string } | { kind: 'arxiv'; id: string }

/**
 * A DOI's shape: the `10.` prefix, a registrant, a slash, and a suffix with no
 * whitespace in it. Deliberately not a full grammar — DOI suffixes are almost
 * unconstrained — but strict enough that prose, a bare number and a URL to
 * something else are all refused rather than sent to a resolver as though they
 * were identifiers.
 */
const DOI_SHAPE = /^10\.\d{4,9}\/\S+$/
/** The post-2007 form: `2401.12345`, four or five digits after the point, with an
 *  optional version. */
const ARXIV_NEW = /^\d{4}\.\d{4,5}(v\d+)?$/i
/** The pre-2007 form: an archive, an optional subject class, and seven digits —
 *  `hep-th/9901001`, `math.GT/0309136`. */
const ARXIV_OLD = /^[a-z][a-z-]*(\.[a-z]{2})?\/\d{7}(v\d+)?$/i

/** The URL and scheme forms, each naming which family follows it, so
 *  `arxiv.org/abs/10.1/x` is refused rather than read as a DOI. */
const PREFIXES: readonly (readonly [RegExp, PaperRef['kind']])[] = [
  [/^(?:https?:\/\/)?(?:dx\.)?doi\.org\//i, 'doi'],
  [/^doi:\s*/i, 'doi'],
  [/^(?:https?:\/\/)?(?:www\.)?arxiv\.org\/(?:abs|pdf)\//i, 'arxiv'],
  [/^arxiv:\s*/i, 'arxiv'],
]

/**
 * One typed reference, normalised — or null, which is every other string.
 *
 * REFUSED RATHER THAN REPAIRED when the input is not well-formed UTF-16, which is
 * `liveOriginUrl`'s reasoning in `core/sources.ts` and not the sweep
 * `liveSources.ts` performs on a query. A query with half a character removed
 * still searches; an IDENTIFIER with half a character removed is a plausible name
 * for a different work or for none, and `encodeURIComponent` throws on it rather
 * than guessing. So the whole string is checked once, here, before any of it
 * reaches a URL builder.
 *
 * THE VERSION SURVIVES. `2401.12345v2` is a specific revision of a paper and
 * arXiv will serve exactly it; dropping the suffix would quietly fetch a different
 * text from the one somebody asked for.
 *
 * A DOI IS LOWERCASED and an arXiv id is not. DOIs are case-insensitive and
 * `bareDoi` lowercases every one this app compares, so one form out is what keeps
 * a paper fetched here and a record fetched by a check the same work. An old-style
 * arXiv id carries a subject class that is canonically upper-case — `math.GT` —
 * and lowercasing it would print an identifier nobody writes.
 */
export function parsePaperRef(input: string): PaperRef | null {
  const raw = input.trim()
  if (!raw || wellFormed(raw) !== raw) return null

  for (const [prefix, kind] of PREFIXES) {
    if (!prefix.test(raw)) continue
    // A query string or a fragment is how a reader's address bar returns a paper
    // — `arxiv.org/abs/2401.12345v1?context=cs` — and neither is part of the id.
    const rest = raw
      .replace(prefix, '')
      .split(/[?#]/)[0]!
      .replace(/\.pdf$/i, '')
    return asRef(kind, rest)
  }
  // No prefix: the shapes are disjoint, so both are tried and at most one matches.
  return asRef('doi', raw) ?? asRef('arxiv', raw)
}

function asRef(kind: PaperRef['kind'], id: string): PaperRef | null {
  if (kind === 'doi') return DOI_SHAPE.test(id) ? { kind: 'doi', doi: id.toLowerCase() } : null
  if (!ARXIV_NEW.test(id) && !ARXIV_OLD.test(id)) return null
  // Only the version marker is normalised. `V2` and `v2` are the same revision and
  // the canonical spelling is lower-case; everything to its left is left alone.
  return { kind: 'arxiv', id: id.replace(/v(\d+)$/i, 'v$1') }
}

/* ── what comes back ───────────────────────────────────────────────────────── */

/**
 * How much of the paper this document actually holds.
 *
 * The MACHINE half of `provenance`, and it exists for the reason
 * `IndexScanFailure.code` does: a caller deciding what to say — the status line
 * under the field — must not have to read an English sentence to find out what
 * happened. The sentence is for the reader; this is for the code.
 */
export type PaperReading = 'full-text' | 'abstract' | 'metadata'

export type PaperFetch =
  | {
      ok: true
      /** Already carrying its disclosure marks, and already inside `addDoc`'s
       *  clamp so nothing is lost on the way to storage. */
      title: string
      /** The paper, as much of it as arrived. Never the provenance line — that is
       *  joined on by `addPaper`, so a caller can tell the two apart. */
      text: string
      provenance: string
      reading: PaperReading
      /** Present only where Crossref's retraction data said so. `flagged` — a
       *  correction, an erratum, an expression of concern — is a note ON the work
       *  rather than a withdrawal OF it, so it rides in the provenance line where
       *  a reader and a check both see it, and never in this field: `DocNotice` on
       *  a stored corpus document is `liveSources.ts`'s alone to write. */
      notice?: 'retracted'
    }
  | { ok: false; why: string }

export interface PaperOptions {
  /**
   * The rows the user has switched on, already filtered by the caller — the shape
   * `LiveFetchOptions.rows` keeps, so this function never re-decides what somebody
   * turned on. A row absent here is not called: the resolver's absence refuses the
   * whole fetch, and an enricher's absence costs whatever that row would have
   * added. Defaults to every wired row, which is what a caller with no preference
   * to honour means.
   */
  rows?: readonly SourceDef[]
  /** Injectable so the whole suite runs offline, as `LiveFetchOptions.fetchImpl`
   *  is: nothing in this repo has ever reached the network from a test. */
  fetchImpl?: typeof globalThis.fetch
  timeoutMs?: number
  /** The press's signal. Composed into every request by `getJson`/`getXml`, and
   *  re-read between rounds — a request cannot know whether the press it was made
   *  for still exists. */
  signal?: AbortSignal
  /** `nv.src.mailto`, for the polite pools at Crossref and OpenAlex. It
   *  identifies a caller; it authorises nothing. */
  mailto?: string
  /** The clock the fetched-on day is read from. */
  now?: () => number
}

export const PAPER_NOT_A_REFERENCE =
  'That is not a DOI or an arXiv id. A DOI looks like 10.1038/s41586-020-2649-2; an arXiv id looks like 2401.12345 or hep-th/9901001.'

export const PAPER_STOPPED = 'The fetch was stopped before it finished.'

/** The service answered, correctly, and has nothing under that identifier. Not
 *  one of the four transport codes because nothing failed — which is exactly why
 *  it needs a sentence of its own rather than borrowing `malformed`'s. */
const noRecord = (name: string): string => `${name} has no record of that identifier.`

/**
 * A row the user switched off, named with the job it was going to do.
 *
 * The switch has to mean something on this door or it means nothing at all: no
 * source check calls the arXiv row, so if this press ignored the preference too,
 * the row's switch would be decoration on a settings page.
 */
const switchedOff = (row: SourceDef, job: string): string =>
  `${row.name} is switched off in Settings → Sources, and it is what ${job}.`

/**
 * A transport failure, in the reader's words.
 *
 * `http` gets one extra sentence and the others get none, because `getBody`
 * collapses every non-2xx status into that one code: a DOI the resolver has never
 * heard of arrives as a 404 and would otherwise be reported as a service refusing
 * to answer, which points a reader at an outage when what they have is a typo.
 */
const failureNote = (code: LiveFailureCode, name: string, resolver: boolean): string =>
  code === 'http' && resolver
    ? `${LIVE_FAILURE_NOTE.http(name)} An identifier it has no record of also arrives this way.`
    : LIVE_FAILURE_NOTE[code](name)

/* ── the readers ───────────────────────────────────────────────────────────── */

/* Three one-line readers `liveSources.ts` keeps private, copied for the reason
   `indexPiles.ts` copies two of them: a one-liner is not the drift D13 is about —
   there is no rule in any of them to drift — and exporting them would be exporting
   a shape rather than a decision. */
const txt = (v: unknown): string => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '')
const firstOf = (v: unknown): string => (Array.isArray(v) ? txt(v[0]) : txt(v))
const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}

/** An identifier as a path, per SEGMENT. `10.x/suffix` and `hep-th/9901001` both
 *  carry a slash that is part of the NAME, so encoding the string whole would ask
 *  a service for something nobody registered. `pathSafe` in `core/sources.ts` says
 *  the same thing for the same reason; that one is private to the derivation it
 *  guards, and this one guards a request. `parsePaperRef` has already refused
 *  anything not well-formed, which is the precondition `encodeURIComponent` throws
 *  on. */
const idPath = (id: string): string => id.split('/').map(encodeURIComponent).join('/')

/** A url with its query, or without one when there is nothing to put in it: a
 *  bare `?` on the end of every request is a string no service asked for. */
const withQuery = (base: string, parts: Record<string, string | undefined>): string => {
  const q = qs(parts)
  return q ? `${base}?${q}` : base
}

/** What the record says about the work, whoever said it. */
interface PaperMeta {
  title: string
  authors: string
  venue: string
  year: string
  abstract: string
}

/** `{ given, family }`, `{ name }`, or a list too long to be worth printing. */
function authorList(v: unknown): string {
  if (!Array.isArray(v)) return ''
  const names = v
    .map((a) => {
      const one = obj(a)
      return txt(one.name) || [txt(one.given), txt(one.family)].filter(Boolean).join(' ')
    })
    .filter(Boolean)
  if (!names.length) return ''
  return names.length > MAX_AUTHORS
    ? `${names.slice(0, MAX_AUTHORS).join(', ')} and ${names.length - MAX_AUTHORS} others`
    : names.join(', ')
}

const yearOf = (v: unknown): string => {
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? String(Math.trunc(n)) : ''
}

/**
 * Crossref's record of one work.
 *
 * Parsed as defensively as every adapter in `liveSources.ts` — each field
 * optional, nothing assumed about the container beyond `message` being an object —
 * and the ONE required field is the title: a document nobody can name is not a
 * document, and it is also the only thing here that cannot be filled in from
 * somewhere else afterwards.
 */
function readCrossrefWork(body: unknown): PaperMeta | null {
  const message = obj(obj(body).message)
  const title = firstOf(message.title)
  if (!title) return null
  const parts = obj(message.issued)['date-parts']
  return {
    title,
    authors: authorList(message.author),
    venue: firstOf(message['container-title']) || txt(message.type),
    year: yearOf(Array.isArray(parts) && Array.isArray(parts[0]) ? parts[0][0] : undefined),
    // JATS, as the publisher filed it: the tags come off and every word stays
    // where its author put it, so this is quotable in a way a reconstruction is not.
    abstract: stripJats(message.abstract),
  }
}

/** What Europe PMC knows about a DOI: the author's own abstract, and whether the
 *  body may be read. Both halves optional — the row is silent outside biomedicine
 *  and the life sciences, which is why nothing here depends on it. */
interface EpmcRecord {
  abstract: string
  fullTextId?: string
}

function readEpmcSearch(body: unknown, doi: string): EpmcRecord | null {
  const results = obj(obj(body).resultList).result
  // Only a wrong CONTAINER is null. An EMPTY list is the ordinary answer for a
  // paper outside biomedicine and the life sciences, and reporting that as "Europe
  // PMC answered in a shape this build could not read" would put a service's name
  // on a silence that is exactly what the row promises.
  if (!Array.isArray(results)) return null
  const first = obj(results[0])
  // THE ANSWER HAS TO BE ABOUT THE WORK THAT WAS ASKED ABOUT. The query is an exact
  // DOI, so this should never fire — and "should never" is exactly the assumption
  // that makes an abstract of a different paper arrive under this paper's title,
  // where every word of it would be quotable and none of it would be the author's.
  // `bareDoi` folds both sides, so `10.1038/S41586` and `10.1038/s41586` are the
  // one work they are.
  if (bareDoi(first.doi) !== doi) return { abstract: '' }
  const pmcid = txt(first.pmcid)
  return {
    abstract: stripJats(first.abstractText),
    // Open access AND a PMC id, both required, exactly as the adapter reads them:
    // without the first this build has no business reading the body, and without
    // the second there is no address to read it from.
    ...(txt(first.isOpenAccess) === 'Y' && pmcid ? { fullTextId: pmcid } : {}),
  }
}

/** OpenAlex's abstract for a DOI, reassembled. Lossy on punctuation by
 *  construction — see `RECONSTRUCTED_MARK` — which is why it is the LAST abstract
 *  this module will settle for and why taking it changes the title. */
function readOpenAlexAbstract(body: unknown, doi: string): string | null {
  const results = obj(body).results
  if (!Array.isArray(results)) return null
  const work = obj(results[0])
  // The same guard `readEpmcSearch` keeps, for the same reason, one rung further
  // down: a reconstruction of somebody else's abstract is the worst of the four
  // texts this module can end up with and the hardest to notice.
  return bareDoi(work.doi) === doi ? fromInvertedIndex(work.abstract_inverted_index) : ''
}

/**
 * One element's text out of an Atom document, entities decoded.
 *
 * A regex rather than a parser, which is `stripJats`'s own decision and rests on
 * the same fact: this text is going into a keyword index and possibly into a quote
 * the CLIENT slices, so what matters is that no markup reaches a reader — not that
 * the document's structure survives. The `\b` stops `<summary>` from being found
 * by a search for `<sum>`, and the search is scoped to the entry by its caller so
 * that the feed's own `<title>`, which echoes the query, is never mistaken for the
 * paper's.
 */
const atomTag = (xml: string, name: string): string =>
  stripJats(new RegExp(`<${name}\\b[^>]*>([\\s\\S]*?)</${name}>`, 'i').exec(xml)?.[1] ?? '')

/**
 * arXiv's record of one preprint.
 *
 * An error — a malformed id, or one the API will not parse — comes back as a
 * perfectly valid feed carrying an entry whose `id` points into
 * `arxiv.org/api/errors`, and an id that is merely unknown comes back as a feed
 * with no entry at all. Both are "no record of that identifier" here, because the
 * only difference between them is which of two ways the identifier was wrong.
 */
function readArxivEntry(xml: unknown): PaperMeta | null {
  const raw = typeof xml === 'string' ? xml : ''
  const entry = /<entry\b[^>]*>([\s\S]*?)<\/entry>/i.exec(raw)?.[1]
  if (!entry) return null
  if (/arxiv\.org\/api\/errors/i.test(atomTag(entry, 'id'))) return null
  const title = atomTag(entry, 'title')
  if (!title) return null
  const names = [...entry.matchAll(/<author\b[^>]*>([\s\S]*?)<\/author>/gi)]
    .map((m) => atomTag(m[1] ?? '', 'name'))
    .filter(Boolean)
  return {
    title,
    authors: authorList(names.map((name) => ({ name }))),
    // What arXiv is: the record's venue is the preprint server, and saying so in
    // the passage beats leaving the slot empty. A journal reference, where the
    // record carries one, is the more specific truth and wins.
    venue: atomTag(entry, 'arxiv:journal_ref') || 'arXiv preprint',
    year: yearOf(atomTag(entry, 'published').slice(0, 4)),
    abstract: atomTag(entry, 'summary'),
  }
}

/* ── the requests ──────────────────────────────────────────────────────────── */

/* Every url is built one expression from the request it is for, so the shape sent
   and the shape parsed cannot drift apart — `LiveAdapter.url`'s rule. None of them
   is exported: what a test pins is the request that was actually made, read off
   the injected `fetch`, rather than a string builder agreeing with itself. */

const crossrefWorkUrl = (doi: string, mailto?: string): string =>
  withQuery(`${ROW.crossref.origin}/works/${idPath(doi)}`, { mailto })

/** The FILTER form rather than `/works/openalex:doi:…`, deliberately: a DOI's
 *  slashes would have to survive a path segment, and `qs` percent-encodes a query
 *  value whole, where an encoded slash is uncontroversial. One work is asked for
 *  and one field of it is read. */
const openAlexDoiUrl = (doi: string, mailto?: string): string =>
  withQuery(`${ROW.openalex.origin}/works`, {
    filter: `doi:${doi}`,
    per_page: '1',
    select: 'id,doi,title,abstract_inverted_index',
    mailto,
  })

/** No `mailto`: Europe PMC has no polite pool and no parameter for one, so the
 *  address is simply not sent — the adapter's rule, for the adapter's reason. */
const epmcSearchUrl = (doi: string): string =>
  withQuery(`${ROW.europepmc.origin}/europepmc/webservices/rest/search`, {
    query: `DOI:"${doi}"`,
    format: 'json',
    pageSize: '1',
    resultType: 'core',
  })

/** `pmcid` already carries its `PMC` prefix, so it is the path segment as Europe
 *  PMC returned it and this build never assembles an identifier of its own. */
const epmcFullTextUrl = (pmcid: string): string =>
  `${ROW.europepmc.origin}/europepmc/webservices/rest/${encodeURIComponent(
    wellFormed(pmcid),
  )}/fullTextXML`

const arxivUrl = (id: string): string => withQuery(`${ROW.arxiv.origin}/api/query`, { id_list: id })

/* ── the document ──────────────────────────────────────────────────────────── */

/** The title with every disclosure the record carries appended AFTER the clip, so
 *  clipping a long title costs the title's own tail and never a marker. A
 *  reconstructed abstract of a retracted paper is two facts about it and neither
 *  may push the other off the end — `titleOf`'s rule in `liveSources.ts`, against
 *  `addDoc`'s clamp instead of the codec's. */
/**
 * The title, carrying every fact a reader of one PASSAGE would otherwise never
 * see.
 *
 * All of them in one place, because they are one class: the title is the only
 * field that travels with a selected passage and with a retained cite, so
 * anything of the form "this document is not the paper" has to be here or it is
 * nowhere. What each mark says and why is at its constant in `liveSources.ts`.
 */
function paperTitle(p: {
  title: string
  reconstructed: boolean
  reading: PaperReading
  notice?: DocNotice
  flagged?: boolean
  cut?: boolean
}): string {
  const what = p.reconstructed
    ? RECONSTRUCTED_MARK
    : p.reading === 'abstract'
      ? ABSTRACT_MARK
      : p.reading === 'metadata'
        ? RECORD_MARK
        : ''
  // A retraction outranks a flag: a withdrawn work is not also "corrected".
  const stands = p.notice === 'retracted' ? RETRACTED_MARK : p.flagged ? FLAGGED_MARK : ''
  const marks = `${what}${stands}${p.cut ? CUT_MARK : ''}`
  return `${p.title.slice(0, DOC_TITLE_CHARS - marks.length).trim()}${marks}`
}

/** The record as a paragraph, then the paper. Its own paragraph so that
 *  `passagesOf` can begin a passage at the paper's first sentence rather than
 *  mid-citation. */
function paperText(meta: PaperMeta, body: string): string {
  const cited = [meta.venue, meta.year].filter(Boolean).join(', ')
  const head = [meta.title.replace(/\.$/, ''), meta.authors, cited]
    .filter(Boolean)
    .map((part) => `${part}.`)
    .join(' ')
  return [head, body].filter(Boolean).join(PARAGRAPH)
}

// The READER's day, through `core/dates.ts`: a UTC slice cuts a day out of the
// instant for anyone west of Greenwich, which `dates.scan.test.ts` holds the
// whole tree to.
const isoDay = (t: number): string => calendarDay(t)

/**
 * The first line of the stored document, and the reason it is worth this much
 * care: it is what a source check reads before it reads the paper. A passage of
 * this document may be quoted back at a claim, so what the document IS — the
 * body, an abstract, or a catalogue record — has to travel with it rather than
 * living in a UI that is not there at the time.
 *
 * One line, several sentences, and never a newline: a blank line here would make
 * the provenance its own passage and let a check cite this app's own sentence as
 * though it were the paper's.
 */
function provenanceLine(parts: {
  where: string
  door: string | null
  day: string
  notes: readonly string[]
}): string {
  return [`${parts.where}, fetched ${parts.day}.`, parts.door, ...parts.notes]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .slice(0, PROVENANCE_CHARS)
}

/* ── fetching one paper ────────────────────────────────────────────────────── */

/**
 * One paper, or the reason there is none. Never throws.
 *
 * The DOI path is three ROUNDS and up to five requests, which is not the same
 * number: Crossref first and alone, because its title is the one field nothing
 * else can supply; then the two questions that need only the DOI — has anything
 * been filed against this work, and does Europe PMC hold it; then the two that
 * round two made answerable — the open-access body, and a reconstruction of the
 * abstract where nothing else carried one. Requests inside a round do not depend
 * on each other and are issued together, so the worst case grows by one timeout
 * per ROUND rather than one per request.
 *
 * WHICH TEXT WINS, in order, and the order is a claim about how much of the paper
 * each copy carries and how quotable it is:
 *
 *  1. Europe PMC's open-access full text. The paper's own body, so a quote sliced
 *     from it is a sentence its author wrote.
 *  2. Europe PMC's abstract — the author's own text, as the publisher filed it.
 *  3. Crossref's abstract — the same, in JATS, from the record rather than the
 *     index. Above OpenAlex and below Europe PMC for one reason: it is unrebuilt.
 *  4. OpenAlex's reconstruction, which is lossy on punctuation and therefore
 *     changes the TITLE as well as the provenance line. D23's requirement, made
 *     mechanical in `RECONSTRUCTED_MARK`, applies here exactly as it does to a
 *     record a check fetched.
 *  5. The record alone, said out loud as such.
 *
 * A FAILURE BELOW THE RESOLVER IS ADVISORY. Crossref failing costs the whole
 * fetch, because without a title there is no document; Europe PMC, OpenAlex or the
 * retraction lookup failing costs what that row would have added, and the loss is
 * named in the provenance line. Silence there would let an abstract stand in for a
 * paper whose body simply did not arrive — which is the one way this could
 * overstate what it read.
 */
export async function fetchPaper(ref: PaperRef, opts: PaperOptions = {}): Promise<PaperFetch> {
  const rows = opts.rows ?? SOURCES.filter((s) => s.wired)
  const on = (row: SourceDef): boolean => rows.some((r) => r.id === row.id)
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch
  const resolver = ref.kind === 'doi' ? ROW.crossref : ROW.arxiv
  if (typeof fetchImpl !== 'function')
    return { ok: false, why: LIVE_FAILURE_NOTE.network(resolver.name) }

  const transport: Transport = {
    // The address's own funnel, as in `fetchLiveSources`: made well-formed where
    // it enters, because it is about to meet `encodeURIComponent`, and a settings
    // field being usually typed by hand is a fact about how it is filled rather
    // than a property of the string.
    mailto: wellFormed(opts.mailto ?? '').trim() || undefined,
    fetchImpl,
    timeoutMs: opts.timeoutMs ?? PAPER_TIMEOUT_MS,
    signal: opts.signal,
  }
  const day = isoDay((opts.now ?? Date.now)())
  const stopped = (): PaperFetch => ({ ok: false, why: PAPER_STOPPED })

  if (ref.kind === 'arxiv') {
    if (!on(ROW.arxiv)) return { ok: false, why: switchedOff(ROW.arxiv, 'resolves an arXiv id') }
    const got = await getXml(arxivUrl(ref.id), transport)
    if (opts.signal?.aborted) return stopped()
    if ('code' in got) return { ok: false, why: failureNote(got.code, ROW.arxiv.name, true) }
    const meta = readArxivEntry(got.value)
    if (!meta) return { ok: false, why: noRecord(ROW.arxiv.name) }
    // No full text, ever, and the document says so itself rather than leaving a
    // reader to infer it from an abstract that stops after 200 words. The row
    // serves `prior-art` alone for the same reason.
    const where = meta.abstract
      ? `Abstract and metadata from ${ROW.arxiv.name}`
      : `Metadata only from ${ROW.arxiv.name}`
    return finish({
      meta,
      body: meta.abstract,
      reading: meta.abstract ? 'abstract' : 'metadata',
      reconstructed: false,
      provenance: provenanceLine({
        where,
        // Built here rather than through `liveOriginUrl`, which derives an address
        // from a stored docId and knows nothing about arXiv: this id was validated
        // by `parsePaperRef` one call ago, and `abs` is the page a reader wants.
        // Per segment, because an old-style id is `hep-th/9901001` and that slash
        // is part of the name — see `idPath`.
        door: `https://arxiv.org/abs/${idPath(ref.id)}`,
        day,
        notes: [
          meta.abstract
            ? 'arXiv serves no full text here, and the PDF beside the record is not read.'
            : 'The record carried no abstract.',
        ],
      }),
    })
  }

  if (!on(ROW.crossref))
    return { ok: false, why: switchedOff(ROW.crossref, 'resolves a DOI to a record') }

  const doi = ref.doi
  const resolved = await getJson(crossrefWorkUrl(doi, transport.mailto), transport)
  if (opts.signal?.aborted) return stopped()
  if ('code' in resolved)
    return { ok: false, why: failureNote(resolved.code, ROW.crossref.name, true) }
  const meta = readCrossrefWork(resolved.value)
  if (!meta) return { ok: false, why: noRecord(ROW.crossref.name) }

  /* Round two. Two questions about the same DOI, neither depending on the other,
     so they are issued together and the worst case grows by ONE timeout rather
     than two: whether anything has been filed against this work, and whether
     Europe PMC holds it. */
  const [retraction, epmc] = await Promise.all([
    fetchRetractions([doi], transport),
    on(ROW.europepmc) ? getJson(epmcSearchUrl(doi), transport) : undefined,
  ])
  if (opts.signal?.aborted) return stopped()

  const notes: string[] = []
  // "We could not find out" must never read as "not retracted" — D31's rule, and
  // the mark is a fact about the work rather than about this pass, so the notices
  // that DID arrive still stand while the sentence says the lookup did not finish.
  if (retraction.failure)
    notes.push(
      `Crossref's retraction data did not answer, so whether this work still stands is unknown.`,
    )
  const notice = retraction.notices.get(doi)
  if (notice === 'retracted')
    notes.push('RETRACTED: Crossref carries a retraction notice for this work.')
  if (notice === 'flagged')
    notes.push('Crossref carries a correction or an expression of concern for this work.')

  let record: EpmcRecord | null = null
  if (epmc) {
    if ('code' in epmc) notes.push(failureNote(epmc.code, ROW.europepmc.name, false))
    else {
      record = readEpmcSearch(epmc.value, doi)
      if (!record) notes.push(LIVE_FAILURE_NOTE.malformed(ROW.europepmc.name))
    }
  }

  /* Round three: the paper's body, and the abstract of last resort. Both are
     questions round two made answerable, neither depends on the other, and both
     are conditional on what round two actually found — so they go together.

     THE RECONSTRUCTION IS ASKED FOR ONLY INTO A GAP. OpenAlex's abstract is the
     one this machine has to rebuild from a word index, so requesting it while an
     unrebuilt abstract is already in hand is bytes spent on text that could only
     lose. It is asked when neither Crossref nor Europe PMC carried one — which is
     a question round two answers and round one could not, and the whole reason
     this request sits here rather than up there.

     It is still asked ALONGSIDE a full text rather than after one, which is not a
     contradiction: a body that is coming may not arrive, and an abstract fetched
     in the same round is a fallback in hand when it does not. That is
     `holdFallback`'s shape in `liveSources.ts` — a promise outranks nothing until
     it is kept — bought here for no extra timeout. */
  const wantOpenAlex = !meta.abstract && !record?.abstract && on(ROW.openalex)
  const [full, reconstruction] = await Promise.all([
    record?.fullTextId ? getXml(epmcFullTextUrl(record.fullTextId), transport) : undefined,
    wantOpenAlex ? getJson(openAlexDoiUrl(doi, transport.mailto), transport) : undefined,
  ])
  if (opts.signal?.aborted) return stopped()

  /* A failure here costs the body and nothing else: whatever abstract is already
     in hand still stands, and the note says which half is missing rather than
     letting an abstract pass for a paper. */
  let fullText = ''
  if (full) {
    if ('code' in full) notes.push(failureNote(full.code, ROW.europepmc.name, false))
    else {
      fullText = stripJatsFullText(full.value)
      // An answer with no `<body>` in it is a shape this build could not read,
      // which is what `malformed` means everywhere else here. Silently keeping the
      // abstract would make an outage indistinguishable from a paper with nothing
      // in it.
      if (!fullText) notes.push(LIVE_FAILURE_NOTE.malformed(ROW.europepmc.name))
    }
    if (!fullText)
      notes.push(
        'The open-access full text did not arrive, so this document does not carry the paper’s body.',
      )
  }

  let rebuilt = ''
  if (reconstruction) {
    if ('code' in reconstruction)
      notes.push(failureNote(reconstruction.code, ROW.openalex.name, false))
    else {
      const abstract = readOpenAlexAbstract(reconstruction.value, doi)
      if (abstract === null) notes.push(LIVE_FAILURE_NOTE.malformed(ROW.openalex.name))
      else rebuilt = abstract
    }
  }

  const epmcAbstract = record?.abstract ?? ''
  const chosen: { body: string; where: string; reading: PaperReading; reconstructed: boolean } =
    fullText
      ? {
          body: fullText,
          where: `Full text from ${ROW.europepmc.name} (${record!.fullTextId}), metadata from ${ROW.crossref.name}`,
          reading: 'full-text',
          reconstructed: false,
        }
      : epmcAbstract
        ? {
            body: epmcAbstract,
            where: `Abstract from ${ROW.europepmc.name}, metadata from ${ROW.crossref.name}`,
            reading: 'abstract',
            reconstructed: false,
          }
        : meta.abstract
          ? {
              body: meta.abstract,
              where: `Abstract and metadata from ${ROW.crossref.name}`,
              reading: 'abstract',
              reconstructed: false,
            }
          : rebuilt
            ? {
                body: rebuilt,
                where: `Abstract reconstructed from ${ROW.openalex.name}'s word index, metadata from ${ROW.crossref.name}`,
                reading: 'abstract',
                reconstructed: true,
              }
            : {
                body: '',
                where: `Metadata only from ${ROW.crossref.name}`,
                reading: 'metadata',
                reconstructed: false,
              }

  if (chosen.reconstructed)
    notes.push(
      'The reconstruction is lossy on punctuation, so no sentence in it is the author’s own.',
    )
  if (chosen.reading === 'metadata')
    notes.push('No abstract was available and no open-access full text was found.')

  return finish({
    meta,
    body: chosen.body,
    reading: chosen.reading,
    reconstructed: chosen.reconstructed,
    ...(notice === 'retracted' ? { notice: 'retracted' as const } : {}),
    ...(notice === 'flagged' ? { flagged: true } : {}),
    provenance: provenanceLine({
      where: chosen.where,
      // The one derivation, so a paper fetched here opens at the same address a
      // passage cited from the same work opens at everywhere else in the app.
      door: liveOriginUrl(`doi:${doi}`),
      day,
      notes,
    }),
  })
}

/**
 * The last two things every path does: cut the paper to the room it has, and say
 * so if the cut bit.
 *
 * The cut is measured on the ASSEMBLED text rather than on the body, because the
 * record's own paragraph is part of the document and `addDoc` counts the whole
 * thing. A truncated paper is still the paper — every sentence in it is still the
 * author's — so this is a note rather than a refusal, but a document that stops
 * mid-argument must not do so silently.
 */
function finish(p: {
  meta: PaperMeta
  body: string
  reading: PaperReading
  reconstructed: boolean
  provenance: string
  notice?: 'retracted'
  /** A correction or an expression of concern. Not a `DocNotice` — see
   *  `PaperFetch.notice` — but it still has to reach the title. */
  flagged?: boolean
}): PaperFetch {
  const whole = paperText(p.meta, p.body)
  const text = whole.slice(0, PAPER_CHARS)
  const cut = whole.length > text.length
  const provenance = cut
    ? provenanceAppend(
        p.provenance,
        `Cut to the ${PAPER_CHARS.toLocaleString()}-character limit one document may hold.`,
      )
    : p.provenance
  return {
    ok: true,
    // Every disclosure rides in the TITLE as well as the provenance line,
    // because the line is one paragraph of the document and `retrieve` selects
    // passages: a body chunk chosen for the claim travels without it.
    title: paperTitle({
      title: p.meta.title,
      reconstructed: p.reconstructed,
      reading: p.reading,
      ...(p.notice ? { notice: p.notice } : {}),
      ...(p.flagged ? { flagged: true } : {}),
      cut,
    }),
    text,
    provenance,
    reading: p.reading,
    ...(p.notice ? { notice: p.notice } : {}),
  }
}

/**
 * Appended inside the same ceiling the line was built under, so the reserved
 * headroom stays a promise rather than an estimate — and the SENTENCE is what
 * survives the cut, never the line it is appended to.
 *
 * It used to be `line + ' ' + sentence` sliced to the ceiling, which clips from
 * the end: the appended sentence went first. `provenanceLine` already cuts to
 * `PROVENANCE_CHARS`, so a paper whose enrichment and retraction lookups filled
 * that line lost the disclosure ENTIRELY — and the disclosure is the sentence
 * saying the text below it is a hundred thousand characters of a longer paper.
 * A partial paper presented as whole is the one thing this line exists to
 * prevent, so where the two do not both fit, the older notes give way.
 *
 * Exported for its test: reaching the full case through a fetch means stacking
 * enough enrichment and retraction notes to fill four hundred characters, which
 * would pin the note wording rather than the rule.
 */
export const provenanceAppend = (line: string, sentence: string): string => {
  const room = PROVENANCE_CHARS - sentence.length - 1
  // A sentence too long to share the line takes it: the note about the notes is
  // worth less than the note about the text.
  if (room <= 0) return sentence.slice(0, PROVENANCE_CHARS)
  if (line.length <= room) return `${line} ${sentence}`
  // Cut at the last sentence end that fits, so what remains is whole sentences
  // rather than half of one. A hard cut is the fallback for a line with none.
  const cut = line.lastIndexOf('. ', room)
  const kept = (cut > 0 ? line.slice(0, cut + 1) : line.slice(0, room)).trimEnd()
  return `${kept} ${sentence}`
}

/* ── adding it ─────────────────────────────────────────────────────────────── */

export type AddPaperResult =
  | { ok: true; doc: CorpusDoc; provenance: string; reading: PaperReading; notice?: 'retracted' }
  | { ok: false; why: string }

/**
 * Fetch it, then add it — and every refusal on the way is somebody's own words.
 *
 * `addDoc`'s gates are untouched and are the last word: the character budget, the
 * document count, the duplicate rule and the browser's own storage refusal all
 * apply to a fetched paper exactly as they apply to a paste, and each states its
 * arithmetic. Passing that sentence straight through is the whole of what this
 * function adds to the two calls it makes — a refusal rewritten here would be a
 * limit the user cannot see, described in words that are not the ones the dialog
 * uses everywhere else.
 *
 * The provenance line goes in as the document's first paragraph, so a reader
 * opening the document reads what it is before it reads what the paper says.
 * That paragraph is one CHUNK, though, and `retrieve` selects the passages that
 * match a claim — so the facts a model must not miss ride in the title instead,
 * which travels with every passage and every cite. See `CUT_MARK`.
 */
/**
 * Is this paper's BODY already in the corpus, under whatever provenance?
 *
 * `addDoc` compares whole documents, which is the right rule for a paste and the
 * wrong one for a fetch: this function prefixes a provenance line carrying the
 * UTC day it ran, so the same paper fetched on two days is two different strings
 * and the duplicate check never fires. An advisory lookup landing a new note does
 * the same. `addDoc`'s own comment says what the rule is for — "a corpus holding
 * the same passage twice gives that passage two chances to be retrieved" — and
 * a dated prefix is exactly what defeats it while leaving the passages identical.
 *
 * The BODY and not the ref, which is the other way this could have been done.
 * A ref would need a field on `CorpusDoc` and a codec that admits it, and it
 * would still be answering a question the text can answer on its own: two
 * documents whose passages are identical are the duplicate this rule is about,
 * however each arrived. It also catches the same paper fetched under a DOI once
 * and an arXiv id the next time, which a ref comparison would not.
 *
 * Split on the FIRST paragraph, because that is where `addPaper` puts the
 * provenance and nowhere else — so a body is compared against a body. That is
 * why this is not `addDoc`'s rule, where the first paragraph is content.
 *
 * BOTH forms, and the whole text first. A held document that arrived as a paste
 * or an upload carries no provenance line, so the split eats its real first
 * paragraph and the stripped tail matches nothing. `addDoc`'s own check does not
 * catch it either: by the time it runs, the text it is handed carries a
 * provenance prefix the paste does not, so the two strings differ. The corpus
 * then holds one body twice — the exact thing the rule exists to prevent, and
 * two chances at retrieval for one passage.
 *
 * Refused with its own sentence rather than `addDoc`'s, because the action
 * differs. A double paste wants "you already added this"; a re-fetch usually
 * means somebody wants the CURRENT record — a retraction notice, a fuller text —
 * and the only way to get it is to remove the copy that is there.
 */
function paperAlreadyHeld(body: string): string | null {
  const want = body.replace(/\r\n/g, '\n').trim()
  if (!want) return null
  for (const entry of listDocs()) {
    const held = loadDoc(entry.id)
    if (!held) continue
    if (held.text.trim() === want) return entry.title
    const after = held.text.indexOf(PARAGRAPH)
    if (after >= 0 && held.text.slice(after + PARAGRAPH.length).trim() === want) {
      return entry.title
    }
  }
  return null
}

export async function addPaper(ref: PaperRef, opts: PaperOptions = {}): Promise<AddPaperResult> {
  const got = await fetchPaper(ref, opts)
  if (!got.ok) return got
  const twin = paperAlreadyHeld(got.text)
  if (twin) {
    return {
      ok: false,
      why: `You already have this paper, as “${twin}”. Remove it first to add it again with today's record.`,
    }
  }
  const added = addDoc(got.title, `${got.provenance}${PARAGRAPH}${got.text}`)
  if (!added.ok) return added
  return {
    ok: true,
    doc: added.doc,
    provenance: got.provenance,
    reading: got.reading,
    ...(got.notice ? { notice: got.notice } : {}),
  }
}
